import { Repository } from "../utils";
import { InviteCreateRequest, Role } from "./main";

export class InviteRepository extends Repository {
  apiUrl = '/api/v1/invites/';
  invitee_email = '';
  invitee_role = 'member';
  invites : InviteCreateRequest[] = [];

  constructor(public organizationId: number){
    super()
    this.organizationId = organizationId
  }

  addInvite(availableSeats: number | null) {

    if (!availableSeats || availableSeats <= 0) {
      return { success: false, message: 'You have reached the maximum number of invites', type: 'error' };
  }

    if (!this.invitee_email || !/\S+@\S+\.\S+/.test(this.invitee_email)) {
        return { success: false, message: 'A valid email is required', type: 'error' };
    }

    if (this.invites.some(invite => invite.invitee_email === this.invitee_email)) {
        return { success: false, message: 'Email already exists', type: 'error' };
    }

    this.invites.push({invitee_email: this.invitee_email, invitee_role: this.invitee_role as Role});
    this.invitee_email = '';
    this.invitee_role = 'member';

    return { success: true };
}


  async getByOrganization(): Promise<any> {
    const response = await fetch(this.apiUrl + "?object_type=organization&object_id=" + this.organizationId);
    return response.status === 200 ? await response.json().then(data => data):[];
  }

  async createInvites(csrfToken: string): Promise<any> {
    let objectType = "organization";

    const response = await fetch(
      this.apiUrl + "?object_type=" + objectType + "&object_id=" + this.organizationId,
      {
        method: "POST",
        body: JSON.stringify(this.invites),
        headers: {
          "X-CSRFToken": csrfToken,
          "Content-Type": "application/json"
        }
      }
    );

    const responseData = await response.json();

    const successfulInvites = responseData.successful_invites || [];
    const failedInvites = responseData.failed_invites || [];

    // Remove the successfully sent invites
    this.invites = this.invites.filter(invite =>
      !successfulInvites.includes(invite.invitee_email)
    );

    // Only keep invites that failed to send
    this.invites = this.invites.filter(invite =>
      failedInvites.some((fail: { email: string; reason: string }) => fail.email === invite.invitee_email)
    );

    return responseData; // Return the parsed data
  }



  async cancelInvite(inviteId: number, csrfToken: string): Promise<any> {
    let objectType = "organization";

    return await fetch(
        this.apiUrl + inviteId + "/cancel/" + "?object_type=" + objectType + "&object_id=" + this.organizationId,
        {
            method: "PUT",
            headers: {
              "X-CSRFToken": csrfToken,
              "Content-Type": "application/json"
            }
        }
    );
  }


  async resendInvite(inviteId: number, csrfToken: string): Promise<any> {
    let objectType = "organization";

    return await fetch(
        this.apiUrl + inviteId + "/resend/" + "?object_type=" + objectType + "&object_id=" + this.organizationId,
        {
            method: "PUT",
            headers: {
              "X-CSRFToken": csrfToken,
              "Content-Type": "application/json"
            }
        }
    );
  }
}
export class UserRepository extends Repository {
  apiUrl = '/api/v1/members/';

  async getByOrganization(organizationId: number, includeInvites: boolean = true): Promise<any> {
    const response = await fetch(this.apiUrl + "?object_type=organization&object_id=" + organizationId);
    let users = response.status === 200 ? await response.json().then(data => data):[];

    if (includeInvites) {
      const invites = await new InviteRepository(organizationId).getByOrganization();

      const invitesAsPotentialUsers = invites.map((invite: any) => ({
        id: 'invite-' + invite.id,
        name: invite.invitee_email,
        picture: '/static/img/invite.svg',
        email: invite.invitee_email,
        role: `Invited as a${invite.invitee_role === "admin" ? 'n' : ''} ${invite.invitee_role.charAt(0).toUpperCase() + invite.invitee_role.slice(1)}`,
        is_org_admin: false,
        is_active: false,
        email_verified: false,
        status: {
          'invite_status': (invite.expires_in.includes('ago') ? 'expired' : (invite.status || 'pending')),
          'expires_in': invite.expires_in,
        }
        }));
      users = [...users, ...invitesAsPotentialUsers];
    }

    return users;
  }

  async activateMember(objectType: string, objectId: number, memberId: number, csrfToken: string): Promise<any> {
    return await fetch(
      this.apiUrl + memberId + "/activate/" + "?object_type=" + objectType + "&object_id=" + objectId,
      {
          method: "PUT",
          body: JSON.stringify({userId: memberId}),
          headers: {
            "X-CSRFToken": csrfToken,
            "Content-Type": "application/json"
          }
      }
  );
  }

  async getByProject(projectId: number): Promise<any> {
    const response = await fetch(this.apiUrl + "?object_type=project&object_id=" + projectId);
    return response.status === 200 ? await response.json().then(data => data):[];
  }

  async addMember(objectType: string, objectId: number, userId: number, csrfToken: string): Promise<any> {
    return await fetch(
        this.apiUrl + "?object_type=" + objectType + "&object_id=" + objectId,
        {
            method: "POST",
            body: JSON.stringify({new_user_id: userId}),
            headers: {
              "X-CSRFToken": csrfToken,
              "Content-Type": "application/json"
            }
        }
    );
  }

  async removeMember(objectType: string, objectId: number, memberId: number, csrfToken: string): Promise<any> {
    return await fetch(
        this.apiUrl + memberId + "?object_type=" + objectType + "&object_id=" + objectId,
        {
            method: "DELETE",
            headers: {
              "X-CSRFToken": csrfToken,
              "Content-Type": "application/json"
            }
        }
    );
  }

  async changeRole(objectType: string, objectId: number, memberId: number, csrfToken: string, role: string): Promise<any> {
    return await fetch(
        this.apiUrl + memberId + "/change_role/" + "?object_type=" + objectType + "&object_id=" + objectId,
        {
            method: "PUT",
            body: JSON.stringify({role: role}),
            headers: {
              "X-CSRFToken": csrfToken,
              "Content-Type": "application/json"
            }
        }
    );
  }
}
